import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import styles from "@/components/library/ReaderVisual.module.css";
import { Button } from "@/components/ax/Button";
import { Sheet } from "@/components/ax/Sheet";
import { Article } from "@/components/library/Article";
import { HighlightSheet } from "@/components/library/HighlightSheet";
import { ReaderBar, Toc } from "@/components/library/ReaderBar";
import { useFlash } from "@/hooks/useFlash";
import { useI18n } from "@/i18n";
import { getBook, getChapter, listHighlights, fetchHighlights, saveHighlight, persistProgress, persistBookmark } from "@/lib/libraryStore";
import { getSessionUser } from "@/lib/mockAuth";

const pref = (k, d) => localStorage.getItem(k) ?? d;

const Skeleton = () => (
  <div aria-busy="true" data-testid="reader-skeleton" className={`flex flex-col gap-3 ${styles.skeleton}`}>
    {[100, 92, 80].map((w) => (
      <div key={w} className="h-2 rounded-[8px] bg-ax-border" style={{ width: `${w}%` }} />
    ))}
  </div>
);

export default function Reader() {
  const { t, lang } = useI18n();
  const { bookId, chapterId } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [lockedBook, setLockedBook] = useState(null);
  const [size, setSize] = useState(() => Number(pref("axoris.reader.size", "16")));
  const [theme, setTheme] = useState(() => pref("axoris.reader.theme", "light"));
  const [bookmarked, setBookmarked] = useState(false);
  const [savingBookmark, setSavingBookmark] = useState(false);
  const savingRef = useRef(false);
  const currentChapter = useRef(null);
  currentChapter.current = `${bookId}/${chapterId}`;
  const [highlights, setHighlights] = useState([]);
  const [pending, setPending] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const [hint, flash] = useFlash(2000);
  const translate = useRef(t);
  translate.current = t;
  const user = getSessionUser();

  const load = useCallback(async () => {
    setState("loading");
    const res = await getChapter(bookId, chapterId);
    if (res.error === "locked") {
      const b = await getBook(bookId);
      setLockedBook(b.book ?? null);
      return setState("locked");
    }
    if (res.error) return setState(res.error === "missing" ? "missing" : "error");
    setData(res);
    setBookmarked(res.bookmark?.chapterId === chapterId);
    setHighlights(listHighlights(bookId, chapterId));
    fetchHighlights(bookId, chapterId).then((items) => {
      if (items) setHighlights(items);
    });
    if (res.progress?.chapterId !== chapterId) {
      const target = `${bookId}/${chapterId}`;
      persistProgress(bookId, chapterId, 0).then(result => {
        if (currentChapter.current === target && result.error) flash(translate.current("reader.progressFail"));
      });
    }
    setState("ready");
  }, [bookId, chapterId, flash]);

  useEffect(() => {
    load();
  }, [load]);

  const setSizePref = (s) => {
    setSize(s);
    localStorage.setItem("axoris.reader.size", String(s));
  };
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("axoris.reader.theme", next);
  };

  const onSelect = (text) => {
    if (text.length > 280) flash(t("highlight.invalid"));
    else if (text.length >= 8) setPending(text);
  };

  const onSave = async ({ text, color, remark }) => {
    const res = await saveHighlight({ bookId, chapterId, text, color, remark });
    if (res.error) return false;
    setHighlights(listHighlights(bookId, chapterId));
    setPending(null);
    window.getSelection()?.removeAllRanges();
    return true;
  };

  const onBookmark = async () => {
    if (savingRef.current) return;
    const target = `${bookId}/${chapterId}`;
    savingRef.current = true;
    setSavingBookmark(true);
    const result = await persistBookmark(bookId, chapterId);
    if (currentChapter.current === target) {
      if (result.error) flash(t("reader.bookmarkFail"));
      else setBookmarked(result.bookmark?.chapterId === chapterId);
    }
    savingRef.current = false;
    setSavingBookmark(false);
  };
  const onProgressError = useCallback(() => flash(t("reader.progressFail")), [flash, t]);

  if (state === "loading") return <div data-testid="reader-loading"><Skeleton /></div>;

  if (state === "locked") {
    const title = lockedBook?.chapters.find((c) => c.id === chapterId)?.title[lang] ?? lockedBook?.title[lang] ?? "";
    return (
      <div data-testid="reader-locked" className={styles.state}>
        <h1 className="type-h1 [overflow-wrap:anywhere]">{title}</h1>
        <Lock size={24} strokeWidth={1.5} aria-hidden="true" className="mt-6 text-ax-muted" />
        <p className="m-0 mt-3 text-base leading-6 text-ax-text">{t("lib.locked")}</p>
        <Link to={`/library/${bookId}`} className="mt-2 inline-flex min-h-[48px] items-center text-sm font-medium text-ax-link hover:underline">
          {t("reader.back")}
        </Link>
      </div>
    );
  }

  if (state !== "ready") {
    return (
      <div role="alert" data-testid="reader-error" className={styles.state}>
        <p className="m-0 text-base text-ax-text">{t(state === "missing" ? "chapter.notFound" : "chapter.fail")}</p>
        {state === "error" && <Button onClick={load} data-testid="reader-retry-button" className="mt-4">{t("retry")}</Button>}
      </div>
    );
  }

  const { book, chapter, prev, next } = data;
  const dark = theme === "dark";
  const start = data.progress?.chapterId === chapterId ? data.progress.blockIndex : 0;
  const wm = [user.name, user.email].filter(Boolean).join(" · ") || "AXORIS";
  const empty = chapter.body[lang].every((block) => !block.trim());

  return (
    <div data-testid="reader-page" className={styles.page}>
      <Link to={`/library/${bookId}`} data-testid="reader-back-link" className={styles.back}>
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        {t("reader.back")}
      </Link>
      <ReaderBar
        size={size}
        onSize={setSizePref}
        theme={theme}
        onTheme={toggleTheme}
        bookmarked={bookmarked}
        onBookmark={onBookmark}
        savingBookmark={savingBookmark}
        onToc={() => setTocOpen(true)}
      />
      <p role="status" data-testid="reader-hint" className={styles.hint}>
        {hint}
      </p>
      <div className={styles.layout} data-testid="reader-layout">
        <aside className={styles.aside} aria-label={t("reader.toc")}>
          <div className={styles.contents}>
            <h2 className={styles.tocHeading}>{t("reader.toc")}</h2>
            <Toc book={book} current={chapterId} />
          </div>
        </aside>
        <div className={styles.reading}>
          <div
            data-testid="reader-canvas"
            data-reader-theme={dark ? "dark" : "light"}
            className={styles.canvas}
          >
            <p className={styles.bookTitle} data-testid="reader-book-title">{book.title[lang]}</p>
            <h1 className={styles.chapterTitle} data-testid="reader-chapter-title">{chapter.title[lang]}</h1>
            <p className={styles.chapterMeta}>{t("lib.estimatedMinutes", { n: chapter.minutes })}</p>
            <div aria-hidden="true" data-testid="reader-watermark" className="pointer-events-none sticky top-[45vh] z-[1] h-0 select-none">
              <div className="flex justify-center">
                <span className="whitespace-nowrap font-mono text-lg" style={{ opacity: 0.1, transform: "rotate(-12deg)" }}>
                  {wm}
                </span>
              </div>
            </div>
            {empty && <p className={styles.empty} data-testid="reader-empty">{t("reader.empty")}</p>}
            <Article
              bookId={bookId}
              chapterId={chapterId}
              blocks={chapter.body[lang]}
              highlights={highlights}
              startBlock={start}
              size={size}
              onSelect={onSelect}
              onBlockedCopy={() => flash(t("highlight.invalid"))}
              onProgressError={onProgressError}
            />
          </div>
          <nav className={styles.navigation} aria-label={t("book.chapters")}>
            {prev ? (
              <Link to={`/library/${bookId}/bab/${prev}`} data-testid="reader-prev" className="inline-flex min-h-[44px] items-center text-sm font-medium text-ax-link hover:underline">
                <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
                {t("reader.prev")}
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/library/${bookId}/bab/${next}`} data-testid="reader-next" className="inline-flex min-h-[44px] items-center text-right text-sm font-medium text-ax-link hover:underline">
                {t("reader.next")}
                <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
              </Link>
            ) : <span />}
          </nav>
        </div>
      </div>

      <Sheet open={tocOpen} onOpenChange={setTocOpen} title={t("reader.toc")} testId="toc-sheet">
        <Toc book={book} current={chapterId} onPick={() => setTocOpen(false)} />
      </Sheet>
      {pending && <HighlightSheet text={pending} onClose={() => setPending(null)} onSave={onSave} />}
    </div>
  );
}
